/**
 * Phase 16 — Ticket Business-State Machine (client mirror).
 *
 * Mirror of the canonical DB transition table enforced by
 * `_apply_business_state_transition` and the `ticket_business_state_guard`
 * trigger. The DB is the ultimate authority — this client copy exists only
 * to gate UI buttons and label states. Never trust the client.
 *
 * Canonical chain:
 *   draft → estimating → awaiting_approval → approved → in_progress
 *         → work_complete → ready_to_invoice → invoiced → paid → closed
 *
 * Allowed regressions (all require manager/owner + reason):
 *   invoiced → ready_to_invoice
 *   paid → invoiced
 *   closed → paid           (refund, balance still ≥ 0)
 *   closed → invoiced       (refund, balance > 0)
 *   closed → in_progress    (reopen)
 *   voided → draft          (manager force-reset)
 *   any → draft             (manager force-reset)
 *
 * Voiding: any non-terminal state → voided (manager + reason).
 */

export type TicketBusinessState =
  | 'draft'
  | 'estimating'
  | 'awaiting_approval'
  | 'approved'
  | 'in_progress'
  | 'work_complete'
  | 'ready_to_invoice'
  | 'invoiced'
  | 'paid'
  | 'closed'
  | 'voided';

export type TransitionKind = 'forward' | 'regression' | 'manual_override' | 'auto';

const FORWARD_TRANSITIONS: Record<TicketBusinessState, TicketBusinessState | null> = {
  draft: 'estimating',
  estimating: 'awaiting_approval',
  awaiting_approval: 'approved',
  approved: 'in_progress',
  in_progress: 'work_complete',
  work_complete: 'ready_to_invoice',
  ready_to_invoice: 'invoiced',
  invoiced: 'paid',
  paid: 'closed',
  closed: null,
  voided: null,
};

const REGRESSIONS: Array<[TicketBusinessState, TicketBusinessState]> = [
  ['invoiced', 'ready_to_invoice'],
  ['paid', 'invoiced'],
  ['closed', 'paid'],
  ['closed', 'invoiced'],
  ['closed', 'in_progress'],
  ['voided', 'draft'],
];

export function isForward(from: TicketBusinessState, to: TicketBusinessState): boolean {
  return FORWARD_TRANSITIONS[from] === to;
}

export function isRegression(from: TicketBusinessState, to: TicketBusinessState): boolean {
  if (to === 'draft' && from !== 'draft') return true;
  return REGRESSIONS.some(([f, t]) => f === from && t === to);
}

export function canVoidFrom(from: TicketBusinessState): boolean {
  return from !== 'voided';
}

export function canTransition(from: TicketBusinessState, to: TicketBusinessState): boolean {
  if (from === to) return true;
  if (to === 'voided') return canVoidFrom(from);
  if (from === 'voided') return to === 'draft';
  return isForward(from, to) || isRegression(from, to);
}

export function nextForwardState(from: TicketBusinessState): TicketBusinessState | null {
  return FORWARD_TRANSITIONS[from] ?? null;
}

export function isTerminal(state: TicketBusinessState): boolean {
  return state === 'closed' || state === 'voided';
}

export function stateLabel(state: TicketBusinessState | string): string {
  switch (state) {
    case 'draft': return 'Draft';
    case 'estimating': return 'Estimating';
    case 'awaiting_approval': return 'Awaiting Approval';
    case 'approved': return 'Approved';
    case 'in_progress': return 'In Progress';
    case 'work_complete': return 'Work Complete';
    case 'ready_to_invoice': return 'Ready to Invoice';
    case 'invoiced': return 'Invoiced';
    case 'paid': return 'Paid';
    case 'closed': return 'Closed';
    case 'voided': return 'Voided';
    default: return String(state);
  }
}

export function stateOrder(state: TicketBusinessState): number {
  const order: TicketBusinessState[] = [
    'draft', 'estimating', 'awaiting_approval', 'approved',
    'in_progress', 'work_complete', 'ready_to_invoice',
    'invoiced', 'paid', 'closed',
  ];
  const idx = order.indexOf(state);
  return idx === -1 ? 99 : idx;
}
